import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { useFormik } from 'formik';
import { deleteRequest, downloadBlobGet, fetchTextGet, getRequest, postRequest } from '../../../api/ApiController';
import {
  AddIcon,
  CopyIcon,
  DeleteIcon,
  EditIcon,
  EditRowIcon,
  ExportIcon,
  FavoriteIcon,
  ForwardIcon,
  ForwardToLastIcon,
  PriceIcon,
  PrintIcon,
  RefreshIcon,
  SearchIcon,
  VerticalLineIcon,
  BackToFirstIcon,
  BackIcon,
} from '../../svg/svgIcons';
import { Dialog } from 'primereact/dialog';
import AuthContext from '../../../components/AuthContext';
import { LocaleContext } from '../../../components/LocaleContext';
import UtilContext from '../../../components/UtilContext';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import { OverflowMenuProvider } from '../../../components/toolbar/OverflowMenu';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { PanelMenu } from 'primereact/panelmenu';
import { Checkbox } from 'primereact/checkbox';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { Dropdown } from 'primereact/dropdown';
import { Avatar } from 'primereact/avatar';
import { DateTime } from 'luxon';
import { getCurrency } from '../../../utils/moduleSettings';
import { formatCurrency } from '../../../utils/currencyUtils';
import { getEffectiveMask } from '../../../utils/effectiveMask';
import { applyUserMaskRowToTabulator, cleanSorters, safeParse } from '../../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../../utils/tabulatorPersistLayout';
import { getTabulatorLayoutForStandardMask } from '../../../utils/tabulatorLayoutUtils';
import { inputOnEnterHeaderFilter } from '../../../utils/inputOnEnterHeaderFilter';
import { dateUserHeaderFilter } from '../../../utils/dateUserHeaderFilter';
import { tabulatorColumnMinWidthFromTitle, tabulatorDateUserColumnMinWidth, TABULATOR_LIST_ID_MIN_WIDTH } from '../../../utils/tabulatorColumnMinWidth';
import PrintOptionsDialog from '../../../components/PrintOptionsDialog';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../../utils/serviceTaskExport';
import { xlsxDownloadFilename } from '../../../utils/exportExcelNaming';
import { MdSearch, MdSearchOff } from 'react-icons/md';

const PAGE_ID = 'OtkCompanyStatisticsPeriodAdd';

const DEFAULT_COLUMNS_VISIBLE_PERIOD_ADD = Object.freeze({
  id: true,
  company_name: true,
  period: true,
  has_stat: true,
  monthly_revenue: true,
  monthly_claim_costs: true,
  sync_claim_costs: true,
  orders_count: true,
  items_count: true,
  created_at: true,
  updated_at: true,
});

const formatDecimalForSubmit = (v) => (v === null || v === undefined || v === '') ? '' : String(v).trim().replace(',', '.');
const formatDecimalForDisplay = (v) => (v === null || v === undefined) ? '' : String(v).replace(',', '.');

function mergeVisibility(saved) {
  const merged = { ...DEFAULT_COLUMNS_VISIBLE_PERIOD_ADD };
  if (saved && typeof saved === 'object') {
    Object.keys(DEFAULT_COLUMNS_VISIBLE_PERIOD_ADD).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_COLUMNS_VISIBLE_PERIOD_ADD };
  return merged;
}

function normalizePeriodAddColumnVisibilityForSave(stateObj) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_PERIOD_ADD)) out[field] = stateObj?.[field] !== false;
  return out;
}

function columnsFromTabulatorForStandardMaskSave(raw) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_PERIOD_ADD)) out[field] = raw?.[field] !== false;
  return out;
}

function clearHeaderFilterForField(table, field) {
  if (!table || field == null) return;
  try { table.setHeaderFilterValue?.(field, '', true); } catch (_) {}
}

function applyDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_COLUMNS_VISIBLE_PERIOD_ADD)) {
    if (visibilityMap[f] === false) {
      clearHeaderFilterForField(table, f);
      table.hideColumn?.(f);
    } else {
      table.showColumn?.(f);
    }
  }
  setTimeout(() => table.redraw?.(true), 0);
}

function countVisibleDataColumns(vis) {
  if (!vis || typeof vis !== 'object') return 0;
  return Object.keys(DEFAULT_COLUMNS_VISIBLE_PERIOD_ADD).filter((k) => vis[k] !== false).length;
}

const normalizeText = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function mapStatToUpdateFormValues(stat) {
  if (!stat?.id) return null;
  return {
    id: stat.id,
    company_id: stat.company_id,
    month: Number(stat.month),
    year: Number(stat.year),
    monthly_revenue: formatDecimalForDisplay(stat.monthly_revenue),
    monthly_claim_costs: formatDecimalForDisplay(stat.monthly_claim_costs),
    orders_count: stat.orders_count ?? '',
    items_count: stat.items_count ?? '',
    sync_claim_costs: !!stat.sync_claim_costs,
  };
}

export default function OtkCompanyStatisticsPeriodAdd({ year: initialYear, month: initialMonth }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const {
    setDialogText,
    setVisible,
    openFavoriteDialog,
    toast,
    setFastView,
    setUserMaskFilter,
    setStandardMaskFilter,
    openChosenNotificationPreferences,
    getDateFormat,
    formatDate,
    getUserInitials,
    getUserColor,
  } = useContext(UtilContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);

  const sortLocale = locale === 'de' ? 'de' : locale === 'en' ? 'en' : 'sk';

  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => ({
    label: t(`OtkCompanyStatisticsPeriodList.Month.${i + 1}`),
    val: i + 1,
  })), [t]);

  const years = useMemo(() => Array.from({ length: 36 }, (_, i) => {
    const y = 2000 + i;
    return { label: String(y), val: y };
  }), []);

  const monthLabel = useCallback((m) => t(`OtkCompanyStatisticsPeriodList.Month.${Number(m)}`) || String(m), [t]);

  const ref = useRef(null);
  const overlayRef = useRef(null);
  const isRestoringRef = useRef(false);
  const submittedSearchValueRef = useRef('');
  const formikUpdateRef = useRef(null);

  const [selectedYear, setSelectedYear] = useState(initialYear ?? '');
  const [selectedMonth, setSelectedMonth] = useState(initialMonth ?? '');

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedStatistics, setSelectedStatistics] = useState(null);

  const [editIcon, setEditIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);

  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE_PERIOD_ADD });
  const [maskVisible, setMaskVisible] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchIconColor, setSearchIconColor] = useState('');
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);

  const canSetStandardMask = user?.permissions?.includes('set standard mask');
  const hasApplicableStandardMask = useMemo(() => {
    const row = (state.standard_mask_filters || []).find((m) => m.page === PAGE_ID);
    if (!row) return false;
    const attributes = safeParse(row.attributes, []);
    if (!Array.isArray(attributes) || attributes.length <= 1) return false;
    const cols = safeParse(row.columns, {});
    const merged = mergeVisibility(cols && typeof cols === 'object' ? cols : {});
    return Object.values(merged).some(Boolean);
  }, [state.standard_mask_filters]);

  const companies = useMemo(() => {
    const base = (state.customers && state.customers.length ? state.customers : state.companies) || [];
    return [...base].sort((a, b) => (a?.name || '').localeCompare(b?.name || '', sortLocale));
  }, [state.customers, state.companies, sortLocale]);

  const statMap = useMemo(() => {
    const m = new Map();
    (state.company_statistics || []).forEach((s) => {
      m.set(`${s.company_id}__${Number(s.year)}__${Number(s.month)}`, s);
    });
    return m;
  }, [state.company_statistics]);

  const rows = useMemo(() => {
    const y = Number(selectedYear);
    const mo = Number(selectedMonth);
    if (!y || !mo) return [];

    return companies.map((c) => {
      const key = `${c.id}__${y}__${mo}`;
      const stat = statMap.get(key);
      const rowId = key;
      return {
        row_id: rowId,
        id: stat?.id ?? null,
        company_id: c.id,
        company_name: c.name,
        year: y,
        month: mo,
        period: `${monthLabel(mo)} ${y}`.trim(),
        monthly_revenue: formatDecimalForDisplay(stat?.monthly_revenue) || '',
        monthly_claim_costs: formatDecimalForDisplay(stat?.monthly_claim_costs) || '',
        orders_count: stat?.orders_count ?? '',
        items_count: stat?.items_count ?? '',
        sync_claim_costs: stat ? !!stat.sync_claim_costs : true,
        has_stat: !!stat,
        created_at: stat?.created_at ?? null,
        updated_at: stat?.updated_at ?? null,
        contractor: stat?.contractor ?? null,
        last_touched_by_user: stat?.last_touched_by_user ?? null,
      };
    });
  }, [companies, statMap, selectedYear, selectedMonth, monthLabel]);

  const reloadListData = useCallback(() => {
    getRequest('/api/company_statistics').then((response) => {
      if (response?.data) dispatch({ type: 'load_company_statistics', newCompanyStatistics: response.data });
    }).catch(() => {});
    getRequest('/api/companies').then((response) => {
      if (response?.data) dispatch({ type: 'load_companies', newCompanies: response.data });
    }).catch(() => {});
  }, [dispatch]);

  const applyToolbarSearchRowFilter = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const needle = normalizeText(String(submittedSearchValueRef.current ?? '').trim());
    if (needle === '') {
      table.clearFilter(true);
      return;
    }
    table.setFilter((data) => normalizeText(JSON.stringify(data)).includes(needle));
  }, [getTable]);

  useEffect(() => {
    if (normalizeText(String(submittedSearchValueRef.current ?? '').trim()) === '') return;
    applyToolbarSearchRowFilter();
  }, [rows, applyToolbarSearchRowFilter]);

  const navigateToRowId = useCallback((rowId) => {
    const table = getTable();
    if (!table || rowId == null) return;
    try {
      const tableRows = table.getRows?.() ?? [];
      const row = tableRows.find((r) => r.getData?.()?.row_id === rowId);
      if (row) {
        row.select?.();
        table.scrollToRow?.(row, 'middle', false);
      }
    } catch (_) {}
  }, [getTable]);

  const [selectedRowId, setSelectedRowId] = useState(null);

  const scrollToFirstRow = useCallback(() => {
    if (!rows.length) return;
    navigateToRowId(rows[0].row_id);
  }, [rows, navigateToRowId]);

  const scrollToLastRow = useCallback(() => {
    if (!rows.length) return;
    navigateToRowId(rows[rows.length - 1].row_id);
  }, [rows, navigateToRowId]);

  const scrollUpRow = useCallback(() => {
    const curId = selectedRowId;
    const idx = rows.findIndex((r) => r.row_id === curId);
    if (idx > 0) navigateToRowId(rows[idx - 1].row_id);
    else if (idx < 0 && rows.length) navigateToRowId(rows[0].row_id);
  }, [rows, selectedRowId, navigateToRowId]);

  const scrollDownRow = useCallback(() => {
    const curId = selectedRowId;
    const idx = rows.findIndex((r) => r.row_id === curId);
    if (idx >= 0 && idx < rows.length - 1) navigateToRowId(rows[idx + 1].row_id);
    else if (idx < 0 && rows.length) navigateToRowId(rows[0].row_id);
  }, [rows, selectedRowId, navigateToRowId]);

  const validate = () => ({});

  const formik = useFormik({
    initialValues: {
      company_id: '', month: '', year: '', monthly_revenue: '', monthly_claim_costs: '', orders_count: '', items_count: '', sync_claim_costs: true,
    },
    enableReinitialize: true,
    validate,
    onSubmit: (values) => {
      const formData = new FormData();
      for (const k of Object.keys(values)) {
        if (k === 'sync_claim_costs') formData.append(k, values[k] ? 1 : 0);
        else if (k === 'monthly_revenue' || k === 'monthly_claim_costs') formData.append(k, formatDecimalForSubmit(values[k]));
        else formData.append(k, values[k] ?? '');
      }
      postRequest('/api/company_statistics', formData, true)
        .then(() => getRequest('/api/company_statistics'))
        .then((response) => {
          dispatch({ type: 'load_company_statistics', newCompanyStatistics: response.data });
          setShowAddDialog(false);
          formik.resetForm();
          toast?.current?.show({
            severity: 'info',
            summary: t('BusinessPartnerList.Toast.DeletedSummary'),
            life: 3000,
            detail: t('Common.Toast.RecordSavedDetail'),
          });
          return getRequest('/api/companies');
        })
        .then((response) => {
          if (response?.data) dispatch({ type: 'load_companies', newCompanies: response.data });
        })
        .catch(() => {
          if (!navigator.onLine) alert(t('ServiceTask.OfflineQueuedAlert'));
          else alert(t('Common.Toast.ActionFailedDetail'));
        });
    },
  });

  const formikUpdate = useFormik({
    initialValues: selectedStatistics == null ? {
      id: '', company_id: '', month: '', year: '', monthly_revenue: '', monthly_claim_costs: '', orders_count: '', items_count: '', sync_claim_costs: true,
    } : mapStatToUpdateFormValues(selectedStatistics) || {
      id: '', company_id: '', month: '', year: '', monthly_revenue: '', monthly_claim_costs: '', orders_count: '', items_count: '', sync_claim_costs: true,
    },
    enableReinitialize: true,
    validate,
    onSubmit: (values) => {
      const formData = new FormData();
      const v = { ...values };
      for (const k of Object.keys(v)) {
        if (k === 'id') continue;
        if (k === 'sync_claim_costs') formData.append(k, v[k] ? 1 : 0);
        else if (k === 'monthly_revenue' || k === 'monthly_claim_costs') formData.append(k, formatDecimalForSubmit(v[k]));
        else formData.append(k, v[k] ?? '');
      }
      postRequest(`/api/company_statistics/update/${values.id}`, formData, true)
        .then(() => getRequest('/api/company_statistics'))
        .then((response) => {
          dispatch({ type: 'load_company_statistics', newCompanyStatistics: response.data });
          setShowEditDialog(false);
          setSelectedStatistics(null);
          formikUpdate.resetForm();
          toast?.current?.show({
            severity: 'info',
            summary: t('BusinessPartnerList.Toast.DeletedSummary'),
            life: 3000,
            detail: t('Common.Toast.RecordUpdatedDetail'),
          });
          return getRequest('/api/companies');
        })
        .then((response) => {
          if (response?.data) dispatch({ type: 'load_companies', newCompanies: response.data });
        })
        .catch(() => {
          if (!navigator.onLine) alert(t('ServiceTask.OfflineQueuedAlert'));
          else alert(t('Common.Toast.ActionFailedDetail'));
        });
    },
  });

  formikUpdateRef.current = formikUpdate;

  const openAddForCompanyRow = useCallback((rowData) => {
    if (!rowData) return;
    formik.resetForm();
    formik.setFieldValue('company_id', rowData.company_id);
    formik.setFieldValue('year', rowData.year);
    formik.setFieldValue('month', rowData.month);
    setShowAddDialog(true);
  }, [formik]);

  const openEditFromRow = useCallback((rowData) => {
    if (!rowData?.id) {
      openAddForCompanyRow(rowData);
      return;
    }
    const stat = (state.company_statistics || []).find((s) => String(s.id) === String(rowData.id)) || rowData;
    const mapped = mapStatToUpdateFormValues(stat);
    if (!mapped) return;
    setSelectedStatistics(stat);
    formikUpdateRef.current.setValues(mapped);
    setShowEditDialog(true);
  }, [state.company_statistics, openAddForCompanyRow]);

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => openEditFromRow(rowData)}><EditRowIcon /></button>
      </div>
    );
  }

  const onSelectRow = useCallback((data) => {
    const arr = Array.isArray(data) ? data : [];
    const count = arr.length;
    const canEdit = user?.permissions?.includes('edit settings');
    const canAdd = user?.permissions?.includes('add settings');
    const canDelete = user?.permissions?.includes('delete settings');
    setEditIcon(!canEdit || count !== 1);
    setCopyIcon(!canAdd || count !== 1);
    const deletable = arr.filter((r) => r?.id);
    setDeleteIcon(!canDelete || deletable.length < 1);
    setSelectedRowId(count === 1 ? arr[0]?.row_id : null);
  }, [user?.permissions]);

  const textHeaderFilter = useCallback((headerValue, rowValue) => {
    if (!headerValue) return true;
    return normalizeText(rowValue).includes(normalizeText(headerValue));
  }, []);

  const hasStatHeaderFilter = useCallback((headerValue, rowValue, rowData) => {
    if (!headerValue) return true;
    const label = rowData?.has_stat ? t('Common.Yes') : t('Common.No');
    return normalizeText(label).includes(normalizeText(headerValue));
  }, [t]);

  const currencySymbol = getCurrency(state.module_settings || []);

  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;

  const dateOrUserFilter = useCallback((headerValue, rowValue, rowData, filterParams) => {
    if (!headerValue) return true;
    const raw = String(headerValue || '').trim();
    if (raw.startsWith('range:')) {
      const p = raw.slice('range:'.length).split(':');
      if (p.length >= 2 && p[0] && p[1]) {
        const from = DateTime.fromFormat(p[0], 'yyyy-MM-dd').startOf('day');
        const to = DateTime.fromFormat(p[1], 'yyyy-MM-dd').endOf('day');
        const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
        return dt.isValid ? dt >= from && dt <= to : false;
      }
      return true;
    }
    const search = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const userObj = filterParams?.name === 'created_at' ? rowData?.contractor : rowData?.last_touched_by_user;
    const uname = userObj?.employee?.name || userObj?.username || '';
    const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
    const dateStr = dt.isValid ? `${dt.toFormat(dateFmt)} ${dt.toFormat(dateTimeFmt)} ${dt.toFormat('yyyy-MM-dd')}` : '';
    const hay = `${uname} ${dateStr}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    return hay.includes(search);
  }, [dateFmt, dateTimeFmt]);

  function CreatedAtStatFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.contractor;
    const raw = rowData?.created_at;
    const text = formatDate ? formatDate(raw, dateTimeFmt, true) : DateTime.fromISO(String(raw ?? '')).toFormat(dateTimeFmt);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? (
          <Avatar
            className="shrink-0"
            title={u?.employee?.name || u?.username}
            label={getUserInitials(u?.employee?.name || u?.username)}
            shape="circle"
            style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  function UpdatedAtStatFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.last_touched_by_user;
    const raw = rowData?.updated_at;
    const text = formatDate ? formatDate(raw, dateTimeFmt, true) : DateTime.fromISO(String(raw || '')).toFormat(dateTimeFmt);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? (
          <Avatar
            className="shrink-0"
            title={u?.employee?.name || u?.username}
            label={getUserInitials(u?.employee?.name || u?.username)}
            shape="circle"
            style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  const columns = useMemo(() => {
    const companyTitle = t('OtkCompanyStatisticsShared.Column.Company');
    const periodTitle = t('OtkCompanyStatisticsPeriodList.Column.Period');
    const hasStatTitle = t('OtkCompanyStatisticsShared.Column.HasStat');
    const monthlyRevTitle = t('OtkCompanyStatisticsShared.Column.MonthlyRevenue').replace(/\{\{currency\}\}/g, currencySymbol);
    const claimTitle = t('OtkCompanyStatisticsShared.Column.MonthlyClaimCosts').replace(/\{\{currency\}\}/g, currencySymbol);
    const syncTitle = t('OtkCompanyStatisticsShared.Column.SyncClaimCostsFromDb');
    const ordersTitle = t('OtkCompanyStatisticsShared.Column.OrdersCount');
    const itemsTitle = t('OtkCompanyStatisticsShared.Column.ItemsCount');
    const createdTitle = t('EmployeeList.Column.CreatedAt');
    const updatedTitle = t('EmployeeList.Column.UpdatedAt');
    const yes = t('Common.Yes');
    const no = t('Common.No');
    return [
      { field: 'rowSelection', formatter: 'rowSelection', titleFormatter: 'rowSelection', hozAlign: 'center', width: 44, headerSort: false, resizable: false, frozen: true, print: false, download: false },
      { title: '', field: 'editor', hozAlign: 'center', width: 44, headerSort: false, formatter: reactFormatter(<EditRowFormatter />), resizable: false, frozen: true, print: false, download: false },
      { title: t('ChecklistHolidayList.Column.Id'), field: 'id', minWidth: TABULATOR_LIST_ID_MIN_WIDTH, widthGrow: 0.5, hozAlign: 'left', sorter: 'number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
      { title: companyTitle, field: 'company_name', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(companyTitle), widthGrow: 1 },
      { title: periodTitle, field: 'period', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(periodTitle), widthGrow: 1 },
      { title: hasStatTitle, field: 'has_stat', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: hasStatHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(hasStatTitle), widthGrow: 0.6, formatter: (cell) => (cell.getValue() ? yes : no) },
      { title: monthlyRevTitle, field: 'monthly_revenue', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(monthlyRevTitle), widthGrow: 1, formatter: (cell) => formatCurrency(cell.getValue(), currencySymbol) },
      { title: claimTitle, field: 'monthly_claim_costs', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(claimTitle), widthGrow: 1, formatter: (cell) => formatCurrency(cell.getValue(), currencySymbol) },
      { title: syncTitle, field: 'sync_claim_costs', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: (hv, rv, rowData) => textHeaderFilter(hv, rowData?.sync_claim_costs ? yes : no), minWidth: tabulatorColumnMinWidthFromTitle(syncTitle), widthGrow: 1, formatter: (cell) => (cell.getValue() ? yes : no) },
      { title: ordersTitle, field: 'orders_count', hozAlign: 'left', sorter: 'number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(ordersTitle), widthGrow: 1 },
      { title: itemsTitle, field: 'items_count', hozAlign: 'left', sorter: 'number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(itemsTitle), widthGrow: 1 },
      { title: createdTitle, field: 'created_at', hozAlign: 'left', minWidth: tabulatorDateUserColumnMinWidth(createdTitle), widthGrow: 1, formatter: reactFormatter(<CreatedAtStatFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'created_at' }, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt }, sorter: 'datetime', sorterParams: { format: 'iso' } },
      { title: updatedTitle, field: 'updated_at', hozAlign: 'left', minWidth: tabulatorDateUserColumnMinWidth(updatedTitle), widthGrow: 1, formatter: reactFormatter(<UpdatedAtStatFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'updated_at' }, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt }, sorter: 'datetime', sorterParams: { format: 'iso' } },
    ];
  }, [currencySymbol, dateFmt, dateTimeFmt, dateOrUserFilter, formatDate, getUserColor, getUserInitials, hasStatHeaderFilter, openEditFromRow, textHeaderFilter, t]);

  const getMaskColumnLabel = useCallback((field) => ({
    id: t('ChecklistHolidayList.Column.Id'),
    company_name: t('OtkCompanyStatisticsShared.Column.Company'),
    period: t('OtkCompanyStatisticsPeriodList.Column.Period'),
    has_stat: t('OtkCompanyStatisticsShared.Column.HasStat'),
    monthly_revenue: t('OtkCompanyStatisticsShared.Column.MonthlyRevenue').replace(/\{\{currency\}\}/g, currencySymbol),
    monthly_claim_costs: t('OtkCompanyStatisticsShared.Column.MonthlyClaimCosts').replace(/\{\{currency\}\}/g, currencySymbol),
    sync_claim_costs: t('OtkCompanyStatisticsShared.Column.SyncClaimCostsFromDb'),
    orders_count: t('OtkCompanyStatisticsShared.Column.OrdersCount'),
    items_count: t('OtkCompanyStatisticsShared.Column.ItemsCount'),
    created_at: t('EmployeeList.Column.CreatedAt'),
    updated_at: t('EmployeeList.Column.UpdatedAt'),
  }[field] || field), [currencySymbol, t]);

  const getPrintColumnsOrder = useCallback(() => {
    const table = getTable();
    const allowed = Object.keys(DEFAULT_COLUMNS_VISIBLE_PERIOD_ADD);
    const fallback = () => allowed.filter((f) => columnsVisible[f] !== false);
    if (!table?.getColumns) return fallback();
    const allowedSet = new Set(allowed);
    const out = [];
    const seen = new Set();
    table.getColumns().forEach((col) => {
      const field = col.getField?.() ?? col.getDefinition?.()?.field;
      if (!field || !allowedSet.has(field)) return;
      if (columnsVisible[field] === false || seen.has(field)) return;
      seen.add(field);
      out.push(field);
    });
    return out.length > 0 ? out : fallback();
  }, [columnsVisible, getTable]);

  const parseHeaderFiltersToParams = useCallback(() => {
    const hf = getTable()?.getHeaderFilters?.() ?? [];
    const out = {};
    hf.forEach((f) => {
      if (!f?.field) return;
      const v = f.value;
      if (v == null || String(v).trim() === '') return;
      out[`filter_${f.field}`] = v;
    });
    return out;
  }, [getTable]);

  const periodExportDocumentTitle = useCallback(() => {
    const y = Number(selectedYear);
    const m = Number(selectedMonth);
    if (!y || !m) return '';
    return t('OtkCompanyStatisticsPeriodAdd.ExportDocumentTitle')
      .replace('{{month}}', monthLabel(m))
      .replace('{{year}}', String(y));
  }, [selectedYear, selectedMonth, monthLabel, t]);

  const buildPeriodMatrixExportQueryString = useCallback((opts = {}) => {
    const y = Number(selectedYear);
    const mo = Number(selectedMonth);
    const params = {
      year: y && mo ? String(y) : undefined,
      month: y && mo ? String(mo) : undefined,
      ...parseHeaderFiltersToParams(),
      toolbar_search: String(submittedSearchValueRef.current ?? '').trim() || undefined,
      sorters: JSON.stringify(cleanSorters(getTable()?.getSorters?.() ?? [])),
    };
    return buildExportQueryStringFromParams(params, opts);
  }, [getTable, parseHeaderFiltersToParams, selectedYear, selectedMonth]);

  const ensurePeriodSelected = useCallback(() => {
    const y = Number(selectedYear);
    const m = Number(selectedMonth);
    if (!y || !m) {
      toast?.current?.show({ severity: 'warn', summary: t('OtkCompanyStatisticsPeriodAdd.Toast.PeriodSummary'), detail: t('OtkCompanyStatisticsPeriodAdd.Toast.SelectMonthYear'), life: 3500 });
      return false;
    }
    return true;
  }, [selectedYear, selectedMonth, t, toast]);

  const runPeriodMatrixPrint = useCallback(async () => {
    if (!ensurePeriodSelected()) return;
    try {
      setPrintChoiceVisible(false);
      setDialogText(t('Common.PreparingPrint'));
      setVisible(true);
      const suffix = buildPeriodMatrixExportQueryString({
        includePrintColumns: true,
        printLayout: printLayoutChoice,
        printOrientation: printOrientationChoice,
        printColumns: getPrintColumnsOrder(),
      });
      const html = await fetchTextGet(`/api/company_statistics/by_period/export/print${suffix}`);
      if (!openPrintHtmlWindow(html)) toast?.current?.show({ severity: 'warn', summary: t('OtkCompanyStatisticsPeriodList.Toast.PrintTitle'), detail: t('Common.EnablePopups'), life: 4000 });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('OtkCompanyStatisticsPeriodList.Toast.PrintTitle'), detail: e?.message || t('OtkCompanyStatisticsPeriodList.Toast.PrintFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildPeriodMatrixExportQueryString, ensurePeriodSelected, getPrintColumnsOrder, printLayoutChoice, printOrientationChoice, setDialogText, setVisible, t, toast]);

  const exportPeriodMatrixXlsx = useCallback(async () => {
    if (!ensurePeriodSelected()) return;
    try {
      setDialogText(t('Common.PreparingExport'));
      setVisible(true);
      const suffix = buildPeriodMatrixExportQueryString({ includePrintColumns: true, printColumns: getPrintColumnsOrder() });
      const defaultXlsx = xlsxDownloadFilename(periodExportDocumentTitle());
      const { blob, filename } = await downloadBlobGet(`/api/company_statistics/by_period/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('OtkCompanyStatisticsPeriodList.Toast.ExportTitle'), detail: e?.message || t('OtkCompanyStatisticsPeriodList.Toast.ExportFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildPeriodMatrixExportQueryString, ensurePeriodSelected, getPrintColumnsOrder, periodExportDocumentTitle, setDialogText, setVisible, t, toast]);

  const closeMenu = () => {
    OverflowMenuProvider.setVisible(null);
    setExpandedKeys({});
  };

  const saveUserMaskColumns = () => {
    const normalized = normalizePeriodAddColumnVisibilityForSave(columnsVisible);
    if (!Object.values(normalized).some(Boolean)) {
      toast?.current?.show({ severity: 'warn', summary: t('OtkCompanyStatisticsPeriodList.Toast.MaskWarnTitle'), detail: t('OtkCompanyStatisticsPeriodList.Toast.SelectOneColumn'), life: 4000 });
      return;
    }
    setUserMaskFilter(PAGE_ID, 'all', normalized, getTable()?.getHeaderFilters?.() ?? [], null, null);
    setColumnsVisible(normalized);
    setMaskVisible(false);
  };

  const confirmSaveStandardMask = () => {
    confirmDialog({
      message: t('OtkCompanyStatisticsPeriodList.SaveStandardMaskMessage'),
      header: t('OtkCompanyStatisticsPeriodList.SaveStandardMaskHeader'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Save'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        const layout = getTabulatorLayoutForStandardMask(getTable());
        if (!layout) return;
        setStandardMaskFilter(PAGE_ID, columnsFromTabulatorForStandardMaskSave(layout.columns), layout.attributes);
      },
    });
  };

  const applyStandardLayout = () => {
    closeMenu();
    const standard = (state.standard_mask_filters || []).find((m) => m.page === PAGE_ID);
    if (!standard) return;
    const attributes = safeParse(standard.attributes, []);
    const jsonColumns = safeParse(standard.columns, {});
    const merged = mergeVisibility(jsonColumns && typeof jsonColumns === 'object' ? jsonColumns : {});
    const colsForSave = normalizePeriodAddColumnVisibilityForSave(merged);
    confirmDialog({
      message: t('OtkCompanyStatisticsPeriodList.ApplyStandardMaskMessage'),
      header: t('OtkCompanyStatisticsPeriodList.ApplyStandardMaskHeader'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Apply'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        setUserMaskFilter(PAGE_ID, 'all', colsForSave, null, attributes, null, () => {
          const table = getTable();
          if (!table) return;
          isRestoringRef.current = true;
          table.setColumnLayout?.(attributes);
          setColumnsVisible(colsForSave);
          applyDataColumnVisibilityToTable(table, colsForSave);
          setTimeout(() => { isRestoringRef.current = false; }, 0);
        });
      },
    });
  };

  function setMask(field, value) {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE_PERIOD_ADD, field)) return;
    const next = { ...DEFAULT_COLUMNS_VISIBLE_PERIOD_ADD, ...columnsVisible, [field]: value };
    if (value === false && countVisibleDataColumns(next) < 1) {
      toast?.current?.show({ severity: 'warn', summary: t('OtkCompanyStatisticsPeriodList.Toast.MaskWarnTitle'), detail: t('OtkCompanyStatisticsPeriodList.Toast.AtLeastOneColumn'), life: 3500 });
      return;
    }
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  function maskSelectAll() {
    const next = { ...DEFAULT_COLUMNS_VISIBLE_PERIOD_ADD };
    Object.keys(next).forEach((k) => { next[k] = true; });
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  function maskDeselectAll() {
    const next = {};
    Object.keys(DEFAULT_COLUMNS_VISIBLE_PERIOD_ADD).forEach((k) => { next[k] = false; });
    next.company_name = true;
    next.period = true;
    next.id = true;
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target)) setExpandedKeys({});
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const items = [{
    key: '0',
    label: t('Common.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      { key: '0_1', label: t('Common.SetFilter'), icon: 'pi pi-filter', command: () => { setUserMaskFilter(PAGE_ID, 'set_filters', null, getTable()?.getHeaderFilters?.() ?? [], null); closeMenu(); }, template: itemRenderer },
      { key: '0_2', label: t('Common.ClearFilter'), icon: 'pi pi-filter-slash', command: () => { setUserMaskFilter(PAGE_ID, 'set_filters', null, [], null); getTable()?.clearHeaderFilter?.(); closeMenu(); }, template: itemRenderer },
      { key: '0_3', label: t('Common.SetSorting'), icon: 'pi pi-sort-alt', command: () => { setUserMaskFilter(PAGE_ID, 'set_sorters', null, null, null, cleanSorters(getTable()?.getSorters?.() ?? [])); closeMenu(); }, template: itemRenderer },
      { key: '0_4', label: t('Common.ClearSorting'), icon: 'pi pi-sort-alt-slash', command: () => { setUserMaskFilter(PAGE_ID, 'set_sorters', null, null, null, []); getTable()?.setSort?.([]); closeMenu(); }, template: itemRenderer },
      { key: '0_5', label: t('Common.SetMask'), icon: 'pi pi-server', command: () => { setMaskVisible(true); closeMenu(); }, template: itemRenderer },
      ...(hasApplicableStandardMask ? [{ key: '0_5s', label: t('Common.StandardMask'), icon: 'pi pi-clone', command: applyStandardLayout, template: itemRenderer }] : []),
      { key: '0_6', label: t('Common.Notifications'), icon: 'pi pi-envelope', command: () => { openChosenNotificationPreferences(PAGE_ID); closeMenu(); }, template: itemRenderer },
    ],
  }];

  const openAddDialog = () => {
    formik.resetForm();
    formik.setFieldValue('year', Number(selectedYear) || '');
    formik.setFieldValue('month', Number(selectedMonth) || '');
    setShowAddDialog(true);
  };

  const copySelected = () => {
    const selected = getTable()?.getSelectedData?.()?.[0];
    if (!selected) return;
    formik.resetForm();
    formik.setFieldValue('company_id', selected.company_id);
    formik.setFieldValue('year', selected.year);
    formik.setFieldValue('month', selected.month);
    formik.setFieldValue('monthly_revenue', formatDecimalForDisplay(selected.monthly_revenue) ?? '');
    formik.setFieldValue('monthly_claim_costs', formatDecimalForDisplay(selected.monthly_claim_costs) ?? '');
    formik.setFieldValue('orders_count', selected.orders_count ?? '');
    formik.setFieldValue('items_count', selected.items_count ?? '');
    formik.setFieldValue('sync_claim_costs', selected.sync_claim_costs ?? true);
    setShowAddDialog(true);
  };

  const editSelected = () => {
    const row = getTable()?.getSelectedData?.()?.[0];
    if (row) openEditFromRow(row);
  };

  const rejectDelete = () => {
    toast?.current?.show({ severity: 'warn', summary: t('BusinessPartnerList.Toast.RejectedSummary'), detail: t('ServiceTask.RequestCancelled'), life: 3000 });
  };

  const acceptDelete = () => {
    const selected = getTable()?.getSelectedData?.() ?? [];
    const toDelete = selected.filter((r) => r?.id);
    if (toDelete.length === 0) return;
    Promise.all(toDelete.map((row) => deleteRequest(`/api/company_statistics/${row.id}`)))
      .then(() => {
        toast?.current?.show({ severity: 'info', summary: t('BusinessPartnerList.Toast.DeletedSummary'), life: 3000, detail: t('BusinessPartnerList.Toast.DeletedDetail') });
        return getRequest('/api/company_statistics');
      })
      .then((response) => {
        if (response?.data) dispatch({ type: 'load_company_statistics', newCompanyStatistics: response.data });
        return getRequest('/api/companies');
      })
      .then((response) => {
        if (response?.data) dispatch({ type: 'load_companies', newCompanies: response.data });
      })
      .catch(() => {
        toast?.current?.show({ severity: 'warn', summary: t('BusinessPartnerList.Toast.RejectedSummary'), detail: t('BusinessPartnerList.Toast.DeleteFailedDetail'), life: 3000 });
      });
  };

  const confirmDelete = () => {
    confirmDialog({
      message: t('EmployeeList.ConfirmDeleteMany'),
      header: t('ServiceTask.ConfirmDeleteTitle'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: acceptDelete,
      reject: rejectDelete,
    });
  };

  const searchTable = () => {
    const normalized = String(searchValue ?? '').trim();
    submittedSearchValueRef.current = normalized;
    setSearchIconColor(normalized === '' ? '' : 'active');
    applyToolbarSearchRowFilter();
    setVisibleSearch(false);
  };

  const periodSelected = Number(selectedYear) > 0 && Number(selectedMonth) > 0;

  return (
    <>
      <ConfirmDialog />
      <div>
        <div className="w-full h-10 flex flex-row bg-[#333]">
          <IntersectionObserverWrapper>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={!user?.permissions?.includes('add settings')} onClick={openAddDialog}><AddIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={copyIcon} onClick={copySelected}><CopyIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={editIcon} onClick={editSelected}><EditIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={deleteIcon} onClick={confirmDelete}><DeleteIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => setPrintChoiceVisible(true)}><PrintIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={searchIconColor !== 'active' ? () => setVisibleSearch(true) : () => { setSearchIconColor(''); setSearchValue(''); submittedSearchValueRef.current = ''; applyToolbarSearchRowFilter(); }}>{searchIconColor === '' ? <MdSearch fill="#fff" className="w-[28px] h-[28px]" /> : <MdSearchOff fill="#fff" className="w-[28px] h-[28px]" />}</button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={exportPeriodMatrixXlsx}><ExportIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={reloadListData} title={t('Common.Refresh')}><RefreshIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={rows.length === 0} onClick={scrollToFirstRow}><BackToFirstIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={rows.length === 0} onClick={scrollUpRow}><BackIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={rows.length === 0} onClick={scrollDownRow}><ForwardIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={rows.length === 0} onClick={scrollToLastRow}><ForwardToLastIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => openFavoriteDialog(t('OtkCompanyStatisticsPeriodList.FavoriteTitle'), PAGE_ID)}><FavoriteIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => setFastView(PAGE_ID, columnsVisible, columns)}><PriceIcon /></button></div>
            {canSetStandardMask ? (
              <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
                <button type="button" title={t('Common.SaveStandardMask')} onClick={confirmSaveStandardMask}>
                  <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
                </button>
              </div>
            ) : null}
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
            <div className="card flex justify-content-center z-[100]" ref={overlayRef}>
              <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
            </div>
          </IntersectionObserverWrapper>
        </div>

        <Splitter className="h-[calc(100vh_-_177px)]" layout="vertical" pt={{ gutter: { className: 'bg-[#333] h-8' }, gutterHandler: { className: 'bg-secondary ' } }}>
          <SplitterPanel className="flex align-items-start justify-content-start overflow-y-auto" minSize={10} size={28}>
            <div className="w-full relative flex flex-col h-full overflow-x-auto">
              <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md space-y-4">
                <h1 className="text-2xl font-semibold text-left text-zinc-900">{t('OtkCompanyStatisticsPeriodList.Tab.ByPeriod')}</h1>
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="space-y-1">
                    <label htmlFor="period-add-month" className="block text-md font-semibold text-gray-800">{t('OtkCompanyStatisticsPeriodList.Column.Month')}</label>
                    <Dropdown inputId="period-add-month" value={selectedMonth} options={months} optionValue="val" optionLabel="label" onChange={(e) => setSelectedMonth(e.value)} placeholder={t('OtkCompanyStatisticsShared.Form.PlaceholderMonth')} className="w-56" />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="period-add-year" className="block text-md font-semibold text-gray-800">{t('OtkCompanyStatisticsPeriodList.Column.Year')}</label>
                    <Dropdown inputId="period-add-year" value={selectedYear} options={years} optionValue="val" optionLabel="label" onChange={(e) => setSelectedYear(e.value)} placeholder={t('OtkCompanyStatisticsShared.Form.PlaceholderYear')} className="w-40" />
                  </div>
                  <p className="text-sm text-gray-600" aria-live="polite">
                    {periodSelected
                      ? t('OtkCompanyStatisticsPeriodAdd.PeriodShown').replace('{{month}}', monthLabel(selectedMonth)).replace('{{year}}', String(selectedYear))
                      : t('OtkCompanyStatisticsPeriodAdd.SelectPeriodHint')}
                  </p>
                </div>
              </div>
            </div>
          </SplitterPanel>
          <SplitterPanel className="flex flex-col align-items-center justify-content-center w-full overflow-y-auto" size={72} style={{ minHeight: '110px' }}>
            <ReactTabulator
              data={rows}
              className="h-[calc(100vh_-_176px)]"
              onRef={(r) => { ref.current = r?.current; }}
              columns={columns}
              events={{
                rowSelectionChanged: onSelectRow,
                tableBuilt: () => {
                  const table = getTable();
                  if (!table) return;
                  const effective = getEffectiveMask(PAGE_ID, state);
                  if (effective?.type === 'user' && effective.userMask) {
                    isRestoringRef.current = true;
                    applyUserMaskRowToTabulator(table, effective.userMask, {
                      setColumnsVisible: (v) => setColumnsVisible(mergeVisibility(v)),
                      clearHeaderFiltersWhenEmpty: true,
                      applyColumnVisibilityFn: (tbl, jc, setVis) => {
                        const merged = mergeVisibility(jc);
                        setVis(merged);
                        applyDataColumnVisibilityToTable(tbl, merged);
                      },
                    });
                    setTimeout(() => { isRestoringRef.current = false; }, 0);
                  } else if (effective?.type === 'standard' && effective.standardMask) {
                    isRestoringRef.current = true;
                    const attributes = safeParse(effective.standardMask.attributes, []);
                    const jsonColumns = safeParse(effective.standardMask.columns, {});
                    if (Array.isArray(attributes) && attributes.length > 1) table.setColumnLayout?.(attributes);
                    if (jsonColumns && typeof jsonColumns === 'object') {
                      const merged = mergeVisibility(jsonColumns);
                      setColumnsVisible(merged);
                      applyDataColumnVisibilityToTable(table, merged);
                    }
                    setTimeout(() => { isRestoringRef.current = false; }, 0);
                  } else {
                    applyDataColumnVisibilityToTable(table, DEFAULT_COLUMNS_VISIBLE_PERIOD_ADD);
                  }
                  setTimeout(() => applyToolbarSearchRowFilter(), 0);
                },
                ...getTabulatorPersistLayoutEvents(PAGE_ID, ref, setUserMaskFilter),
              }}
              options={{ renderHorizontal: 'virtual', layout: 'fitColumns', movableColumns: true, autoResize: true, headerSortTristate: true, filterMode: 'local' }}
            />
          </SplitterPanel>
        </Splitter>
      </div>

      <PrintOptionsDialog visible={printChoiceVisible} onHide={() => setPrintChoiceVisible(false)} orientation={printOrientationChoice} onOrientationChange={setPrintOrientationChoice} layout={printLayoutChoice} onLayoutChange={setPrintLayoutChoice} onConfirm={runPeriodMatrixPrint} />

      <Dialog header={t('OtkCompanyStatisticsPeriodList.SearchDialogTitle')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-col">
          <input value={searchValue} onChange={(e) => setSearchValue(e.target.value)} type="text" className="block w-full px-4 py-2 border rounded-md" />
          <button type="button" onClick={searchTable} className="w-16 mt-3 px-4 py-2 text-white bg-zinc-900 rounded-md"><SearchIcon /></button>
        </div>
      </Dialog>

      <Dialog header={t('OtkCompanyStatisticsList.Tab.EditBusinessData')} visible={showAddDialog} style={{ width: '40vw' }} onHide={() => setShowAddDialog(false)}>
        <form onSubmit={formik.handleSubmit}>
          <div>
            <div className="space-y-1">
              <label htmlFor="period-add-company" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Column.Company')}</label>
              <select
                id="period-add-company"
                name="company_id"
                required
                value={formik.values.company_id}
                onChange={formik.handleChange}
                className="bg-white block w-full px-4 py-2 mt-1 text-grey-700 border rounded-md"
              >
                <option value="">{t('OtkCompanyStatisticsPeriodAdd.SelectCompanyPlaceholder')}</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="mt-3 space-y-1">
              <span className="font-bold text-black block">{t('OtkCompanyStatisticsShared.Form.MonthInYear')}</span>
              <div className="flex gap-2 items-center text-black">
                <Dropdown value={formik.values.month} options={months} optionValue="val" onChange={(e) => formik.setFieldValue('month', e.value)} optionLabel="label" placeholder={t('OtkCompanyStatisticsShared.Form.PlaceholderMonth')} required />
                <Dropdown value={formik.values.year} options={years} optionValue="val" onChange={(e) => formik.setFieldValue('year', e.value)} optionLabel="label" placeholder={t('OtkCompanyStatisticsShared.Form.PlaceholderYear')} required />
              </div>
            </div>
            <div className="mt-3">
              <label htmlFor="period-add-mr" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.MonthlyRevenue').replace(/\{\{currency\}\}/g, currencySymbol)}</label>
              <input id="period-add-mr" name="monthly_revenue" type="text" required onChange={formik.handleChange} value={formik.values.monthly_revenue} className="block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md" />
            </div>
            <div className="mt-3">
              <div className="flex items-center gap-2">
                <Checkbox inputId="sync_claim_costs_create_period" checked={formik.values.sync_claim_costs} onChange={(e) => formik.setFieldValue('sync_claim_costs', e.checked)} />
                <label htmlFor="sync_claim_costs_create_period" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.SyncClaimsFromDb')}</label>
              </div>
              {!formik.values.sync_claim_costs ? (
                <>
                  <label htmlFor="period-add-mcc" className="font-bold text-black mt-2 block">{t('OtkCompanyStatisticsShared.Form.ClaimCostsMonth').replace(/\{\{currency\}\}/g, currencySymbol)}</label>
                  <input id="period-add-mcc" name="monthly_claim_costs" type="text" required onChange={formik.handleChange} value={formik.values.monthly_claim_costs} className="block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md" />
                </>
              ) : (
                <small className="block mt-2 text-gray-600">{t('OtkCompanyStatisticsShared.Form.ClaimCostsAutoHint')}</small>
              )}
            </div>
            <div className="mt-3">
              <label htmlFor="period-add-orders" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.OrdersPerMonth')}</label>
              <input id="period-add-orders" name="orders_count" type="text" required onChange={formik.handleChange} value={formik.values.orders_count} className="block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md" />
            </div>
            <div className="mt-3">
              <label htmlFor="period-add-items" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.ItemsPerMonth')}</label>
              <input id="period-add-items" name="items_count" type="text" required onChange={formik.handleChange} value={formik.values.items_count} className="block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md" />
            </div>
          </div>
          <button type="submit" className="flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-25">{t('OtkCompanyStatisticsShared.Form.SaveState')}</button>
        </form>
      </Dialog>

      <Dialog header={t('OtkCompanyStatisticsShared.Dialog.EditBusinessDataTitle')} visible={showEditDialog} style={{ width: '40vw' }} onHide={() => { setShowEditDialog(false); setSelectedStatistics(null); formikUpdate.resetForm(); }}>
        <form onSubmit={formikUpdate.handleSubmit}>
          <div>
            <div className="space-y-1">
              <span className="font-bold text-black block">{t('OtkCompanyStatisticsShared.Form.MonthInYear')}</span>
              <div className="flex gap-2 items-center text-black">
                <Dropdown value={formikUpdate.values.month} options={months} optionValue="val" onChange={(e) => formikUpdate.setFieldValue('month', e.value)} optionLabel="label" placeholder={t('OtkCompanyStatisticsShared.Form.PlaceholderMonth')} required />
                <Dropdown value={formikUpdate.values.year} options={years} optionValue="val" onChange={(e) => formikUpdate.setFieldValue('year', e.value)} optionLabel="label" placeholder={t('OtkCompanyStatisticsShared.Form.PlaceholderYear')} required />
              </div>
            </div>
            <div className="mt-3">
              <label htmlFor="period-edit-mr" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.MonthlyRevenue').replace(/\{\{currency\}\}/g, currencySymbol)}</label>
              <input id="period-edit-mr" name="monthly_revenue" type="text" required onChange={formikUpdate.handleChange} value={formikUpdate.values.monthly_revenue} className="block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md" />
            </div>
            <div className="mt-3">
              <div className="flex items-center gap-2">
                <Checkbox inputId="sync_claim_costs_update_period" checked={formikUpdate.values.sync_claim_costs} onChange={(e) => formikUpdate.setFieldValue('sync_claim_costs', e.checked)} />
                <label htmlFor="sync_claim_costs_update_period" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.SyncClaimsFromDb')}</label>
              </div>
              {!formikUpdate.values.sync_claim_costs ? (
                <>
                  <label htmlFor="period-edit-mcc" className="font-bold text-black mt-2 block">{t('OtkCompanyStatisticsShared.Form.ClaimCostsMonth').replace(/\{\{currency\}\}/g, currencySymbol)}</label>
                  <input id="period-edit-mcc" name="monthly_claim_costs" type="text" required onChange={formikUpdate.handleChange} value={formikUpdate.values.monthly_claim_costs} className="block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md" />
                </>
              ) : (
                <small className="block mt-2 text-gray-600">{t('OtkCompanyStatisticsShared.Form.ClaimCostsAutoHint')}</small>
              )}
            </div>
            <div className="mt-3">
              <label htmlFor="period-edit-orders" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.OrdersPerMonth')}</label>
              <input id="period-edit-orders" name="orders_count" type="text" required onChange={formikUpdate.handleChange} value={formikUpdate.values.orders_count} className="block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md" />
            </div>
            <div className="mt-3">
              <label htmlFor="period-edit-items" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.ItemsPerMonth')}</label>
              <input id="period-edit-items" name="items_count" type="text" required onChange={formikUpdate.handleChange} value={formikUpdate.values.items_count} className="block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md" />
            </div>
          </div>
          <button type="submit" className="flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-25">{t('OtkCompanyStatisticsShared.Form.SaveState')}</button>
        </form>
      </Dialog>

      <Dialog header={t('OtkCompanyStatisticsPeriodList.MaskDialogTitle')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2 m-1">
            <button type="button" onClick={maskSelectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('OtkCompanyStatisticsPeriodList.MaskSelectAll')}</button>
            <button type="button" onClick={maskDeselectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('OtkCompanyStatisticsPeriodAdd.MaskDeselectAllExceptCompanyAndPeriod')}</button>
          </div>
          {Object.keys(DEFAULT_COLUMNS_VISIBLE_PERIOD_ADD).map((field) => (
            <div key={field} className="flex flex-row border border-black rounded-md p-2 m-1">
              <Checkbox inputId={`otk-period-add-mask-${field}`} onChange={(e) => setMask(field, e.checked)} checked={columnsVisible[field] !== false} />
              <label htmlFor={`otk-period-add-mask-${field}`} className="ml-2 cursor-pointer">{getMaskColumnLabel(field)}</label>
            </div>
          ))}
          <button type="button" onClick={saveUserMaskColumns} className="m-1 px-4 py-2 text-center tracking-wide text-white bg-zinc-900 rounded-md hover:bg-zinc-700">{t('OtkCompanyStatisticsPeriodList.MaskSave')}</button>
        </div>
      </Dialog>
    </>
  );
}
